import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Minus, Trash2, Search, CreditCard, Banknote, Receipt, X, Users, Wine, Utensils, Beer, Package, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import "./pos.css";

const CAT_TABS = [
  { key: "cocktails", label: "Cocktails", icon: Wine },
  { key: "beers", label: "Bières", icon: Beer },
  { key: "snacks", label: "Snacks", icon: Utensils },
  { key: "bottles", label: "Bouteilles", icon: Package },
  { key: "custom", label: "Libre", icon: Sparkles },
];
// Hoisted once — used to skip the "custom" tab which has its own picker.
const CATALOG_TABS = CAT_TABS.filter((t) => t.key !== "custom");

const PAYMENT_LABELS = { cash: "Espèces", card: "Carte bancaire", other: "Autre" };

function priceEur(n) {
  return `${Number(n || 0).toFixed(2)} €`;
}

export default function POS() {
  const [catalog, setCatalog] = useState({ cocktails: [], snacks: [], beers: [], bottles: [] });
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]); // open orders
  const [activeOrder, setActiveOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("cocktails");
  const [search, setSearch] = useState("");
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [cat, tbl, ord] = await Promise.all([
        api.get("/pos/catalog"),
        api.get("/pos/tables"),
        api.get("/pos/orders?status=open&limit=50"),
      ]);
      setCatalog(cat.data);
      setTables(tbl.data.reservations || []);
      setOrders(ord.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openOrder = (o) => setActiveOrder(o);

  const refreshOrder = async (id) => {
    const { data } = await api.get(`/pos/orders/${id}`);
    setActiveOrder(data);
    // also refresh open orders list totals
    setOrders((prev) => prev.map((p) => (p.id === id ? data : p)));
  };

  const addLine = async (line) => {
    if (!activeOrder) {
      toast.error("Ouvrez d'abord une table");
      return;
    }
    try {
      const { data } = await api.post(`/pos/orders/${activeOrder.id}/lines`, line);
      setActiveOrder(data);
      setOrders((prev) => prev.map((p) => (p.id === data.id ? data : p)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur ajout");
    }
  };

  const removeLine = async (lineId) => {
    if (!activeOrder) return;
    try {
      const { data } = await api.delete(`/pos/orders/${activeOrder.id}/lines/${lineId}`);
      setActiveOrder(data);
      setOrders((prev) => prev.map((p) => (p.id === data.id ? data : p)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const bumpLine = async (line, delta) => {
    const nextQty = line.quantity + delta;
    if (nextQty <= 0) return removeLine(line.id);
    // Simplest server contract for phase 1: delete then re-add.
    await removeLine(line.id);
    await addLine({
      kind: line.kind, ref_id: line.ref_id, name: line.name,
      unit_price: line.unit_price, quantity: nextQty, note: line.note,
    });
  };

  const cancelActive = async () => {
    if (!activeOrder) return;
    if (!window.confirm("Annuler cette commande ?")) return;
    try {
      await api.post(`/pos/orders/${activeOrder.id}/cancel`);
      toast.success("Commande annulée");
      setActiveOrder(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  // -------- Filtered catalog for search --------
  const items = useMemo(() => {
    if (tab === "custom") return [];
    const src = catalog[tab] || [];
    const q = search.trim().toLowerCase();
    if (!q) return src;
    return src.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.category || "").toLowerCase().includes(q),
    );
  }, [catalog, tab, search]);

  const tileClick = (item) => {
    addLine({
      kind: tab === "cocktails" ? "cocktail" : tab === "snacks" ? "snack" : tab === "beers" ? "beer" : "bottle",
      ref_id: item.id,
      name: item.name,
      unit_price: Number(item.price || 0),
      quantity: 1,
    });
  };

  const addCustom = () => {
    const price = Number(customPrice);
    if (!customName.trim() || Number.isNaN(price) || price < 0) {
      toast.error("Nom + prix valides requis");
      return;
    }
    addLine({ kind: "custom", ref_id: null, name: customName.trim(), unit_price: price, quantity: 1 });
    setCustomName("");
    setCustomPrice("");
  };

  return (
    <div className="pos-shell" data-testid="pos-page">
      {/* LEFT: catalog + orders */}
      <div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl">POS Saveurs Normandes</h1>
            <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] mt-1">
              {orders.length} commande{orders.length !== 1 ? "s" : ""} en cours
            </p>
          </div>
          <Button
            onClick={() => setShowNewOrder(true)}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6"
            data-testid="pos-new-order-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouvelle table
          </Button>
          <a
            href="/pos/bar"
            className="rounded-full h-12 px-6 border border-border hover:border-primary/50 flex items-center gap-2"
            data-testid="pos-goto-bar-btn"
          >
            <Beer className="w-4 h-4" /> Écran bar
          </a>
          <a
            href="/pos/service"
            className="rounded-full h-12 px-6 border border-border hover:border-primary/50 flex items-center gap-2"
            data-testid="pos-goto-service-btn"
          >
            🧑‍🍳 Écran service
          </a>
          <button
            onClick={() => {
              if (!window.confirm("Fermer la session tablette ? Le prochain serveur devra saisir son PIN.")) return;
              localStorage.removeItem("access_token");
              window.location.reload();
            }}
            className="rounded-full h-12 px-4 text-sm text-muted-foreground hover:text-primary border border-border/40"
            data-testid="pos-logout-btn"
            title="Fin de shift"
          >
            Fin de shift
          </button>
        </div>

        {/* Open orders bar */}
        {orders.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2" data-testid="pos-open-orders">
            {orders.map((o) => (
              <button
                key={o.id}
                onClick={() => openOrder(o)}
                className={`rounded-full px-4 h-11 border ${activeOrder?.id === o.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}
                data-testid={`pos-open-order-${o.table_label}`}
              >
                <span className="font-medium">{o.table_label}</span>
                <span className="ml-2 text-xs opacity-80">{priceEur(o.total)}</span>
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="mb-3 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un item…"
            className="pl-10 h-12 bg-card"
            data-testid="pos-search-input"
          />
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-3 flex-wrap h-auto p-1 justify-start bg-card">
            {CAT_TABS.map(({ key, label, icon: Icon }) => (
              <TabsTrigger key={key} value={key} className="rounded-full px-4 h-10" data-testid={`pos-tab-${key}`}>
                <Icon className="w-4 h-4 mr-2" /> {label}
                {key !== "custom" && (
                  <span className="ml-2 text-[10px] text-muted-foreground">
                    {(catalog[key] || []).length}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATALOG_TABS.map(({ key }) => (
            <TabsContent key={key} value={key} className="mt-0">
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
              ) : items.length === 0 ? (
                <div className="text-muted-foreground py-8 text-sm" data-testid={`pos-empty-${key}`}>
                  Aucun item — {key === "beers" ? "ajoutez des bières via l'API /admin/pos/beers" : "revenez plus tard"}.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {items.map((it) => (
                    <button
                      key={it.id}
                      className="pos-tile"
                      onClick={() => tileClick(it)}
                      disabled={!activeOrder}
                      data-testid={`pos-tile-${key}-${it.id}`}
                    >
                      <div>
                        {it.category && <div className="pos-tile-cat">{String(it.category).replace(/_/g, " ")}</div>}
                        <div className="pos-tile-name">{it.name}</div>
                      </div>
                      <div className="pos-tile-price">{priceEur(it.price)}</div>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}

          <TabsContent value="custom" className="mt-0">
            <div className="rounded-2xl border border-border bg-card p-6 max-w-lg">
              <h3 className="font-display text-xl mb-4">Item personnalisé</h3>
              <div className="grid gap-3">
                <div>
                  <Label htmlFor="custom-name">Nom</Label>
                  <Input id="custom-name" value={customName} onChange={(e) => setCustomName(e.target.value)}
                         placeholder="Ex : Shot maison, Café, …" className="h-12 mt-1" data-testid="pos-custom-name" />
                </div>
                <div>
                  <Label htmlFor="custom-price">Prix (€)</Label>
                  <Input id="custom-price" type="number" step="0.5" min="0" value={customPrice}
                         onChange={(e) => setCustomPrice(e.target.value)} placeholder="4.00"
                         className="h-12 mt-1" data-testid="pos-custom-price" />
                </div>
                <Button onClick={addCustom} disabled={!activeOrder}
                        className="h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="pos-custom-add-btn">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter à la table
                </Button>
                {!activeOrder && (
                  <p className="text-xs text-muted-foreground">Ouvrez d'abord une table pour ajouter des items.</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* RIGHT: cart */}
      <aside className="pos-cart" data-testid="pos-cart">
        <div className="rounded-2xl border border-border bg-card p-4 flex flex-col h-full">
          {!activeOrder ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-muted-foreground py-12 gap-3">
              <Receipt className="w-10 h-10 opacity-40" />
              <p className="text-sm">Aucune table sélectionnée.<br />Cliquez sur <b>Nouvelle table</b> pour commencer.</p>
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Table</div>
                  <div className="font-display text-2xl">{activeOrder.table_label}</div>
                  {activeOrder.num_guests && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Users className="w-3 h-3" /> {activeOrder.num_guests} pers.
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => setActiveOrder(null)} data-testid="pos-close-cart-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex-1 overflow-auto -mx-4 px-4 border-y border-border py-3 space-y-2">
                {activeOrder.lines.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Panier vide — tapez sur un item.</p>
                ) : activeOrder.lines.map((line) => (
                  <div key={line.id} className="flex items-center gap-2" data-testid={`pos-line-${line.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{line.name}</div>
                      <div className="text-xs text-muted-foreground">{priceEur(line.unit_price)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => bumpLine(line, -1)} data-testid={`pos-line-${line.id}-minus`}>
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 text-center text-sm font-medium">{line.quantity}</span>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => bumpLine(line, +1)} data-testid={`pos-line-${line.id}-plus`}>
                        <Plus className="w-3 h-3" />
                      </Button>
                      <div className="w-16 text-right text-sm gold-text font-medium">{priceEur(line.line_total)}</div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeLine(line.id)} data-testid={`pos-line-${line.id}-delete`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="font-display text-3xl gold-text" data-testid="pos-cart-total">{priceEur(activeOrder.total)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="h-12 rounded-full" onClick={cancelActive} data-testid="pos-cancel-order-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Annuler
                  </Button>
                  <Button
                    onClick={() => setShowClose(true)}
                    disabled={activeOrder.lines.length === 0}
                    className="h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="pos-pay-btn"
                  >
                    <Receipt className="w-4 h-4 mr-2" /> Encaisser
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>

      <NewOrderDialog
        open={showNewOrder}
        onClose={() => setShowNewOrder(false)}
        tables={tables}
        onCreated={(o) => { setActiveOrder(o); setOrders((p) => [o, ...p]); setShowNewOrder(false); }}
      />

      {activeOrder && (
        <CloseOrderDialog
          open={showClose}
          onClose={() => setShowClose(false)}
          order={activeOrder}
          onPaid={() => { setShowClose(false); setActiveOrder(null); load(); toast.success("Table encaissée"); }}
        />
      )}
    </div>
  );
}


// -------------- NEW ORDER DIALOG --------------
function NewOrderDialog({ open, onClose, tables, onCreated }) {
  const [tableLabel, setTableLabel] = useState("");
  const [guests, setGuests] = useState("");
  const [reservationId, setReservationId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setTableLabel(""); setGuests(""); setReservationId(null); }
  }, [open]);

  const pickReservation = (r) => {
    setReservationId(r.id);
    setTableLabel(`${r.customer_name || "Client"} · ${r.time_slot}`);
    setGuests(String(r.num_guests || ""));
  };

  const submit = async () => {
    if (!tableLabel.trim()) { toast.error("Nom de la table requis"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/pos/orders", {
        table_label: tableLabel.trim(),
        reservation_id: reservationId,
        num_guests: guests ? Number(guests) : null,
        lines: [],
      });
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pos-new-order-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle table</DialogTitle>
          <DialogDescription>Choisissez une réservation ou saisissez un nom libre.</DialogDescription>
        </DialogHeader>

        {tables.length > 0 && (
          <div className="mb-2">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Réservations aujourd'hui</div>
            <div className="grid gap-1 max-h-40 overflow-auto">
              {tables.map((r) => (
                <button
                  key={r.id}
                  onClick={() => pickReservation(r)}
                  className={`text-left rounded-lg border px-3 py-2 text-sm hover:border-primary ${reservationId === r.id ? "border-primary bg-primary/5" : "border-border"}`}
                  data-testid={`pos-reservation-${r.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{r.customer_name || "Client"}</span>
                    <span className="text-xs text-muted-foreground">{r.time_slot} · {r.num_guests}p</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-3">
          <div>
            <Label>Nom / n° de table</Label>
            <Input value={tableLabel} onChange={(e) => setTableLabel(e.target.value)}
                   placeholder="T1, Bar, Terrasse 2…" className="h-12 mt-1" data-testid="pos-new-order-label" />
          </div>
          <div>
            <Label>Nb personnes</Label>
            <Input type="number" min="1" value={guests} onChange={(e) => setGuests(e.target.value)}
                   className="h-12 mt-1" data-testid="pos-new-order-guests" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="pos-new-order-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ouvrir la table"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------------- CLOSE / SPLIT DIALOG --------------
function CloseOrderDialog({ open, onClose, order, onPaid }) {
  const [mode, setMode] = useState("single"); // single | equal | items
  const [singleMode, setSingleMode] = useState("cash");
  const [equalCount, setEqualCount] = useState(String(order.num_guests || 2));
  const [equalModes, setEqualModes] = useState({}); // idx -> payment_mode
  const [itemAssign, setItemAssign] = useState({}); // line_id -> split_idx
  const [itemSplits, setItemSplits] = useState([{ label: "Part 1", payment_mode: "cash" }]);
  const [submitting, setSubmitting] = useState(false);

  const total = order.total;

  useEffect(() => {
    if (open) {
      setMode("single");
      setSingleMode("cash");
      setEqualCount(String(order.num_guests || 2));
      setEqualModes({});
      setItemAssign({});
      setItemSplits([{ label: "Part 1", payment_mode: "cash" }]);
    }
  }, [open, order.id, order.num_guests]);

  const submit = async () => {
    let splits = [];
    if (mode === "single") {
      splits = [{ amount: total, payment_mode: singleMode, label: null, line_ids: [] }];
    } else if (mode === "equal") {
      const n = Math.max(1, Number(equalCount) || 1);
      const each = Math.floor((total * 100) / n) / 100;
      const remainder = Number((total - each * (n - 1)).toFixed(2));
      splits = Array.from({ length: n }).map((_, i) => ({
        label: `Part ${i + 1}/${n}`,
        amount: i === n - 1 ? remainder : each,
        payment_mode: equalModes[i] || "cash",
        line_ids: [],
      }));
    } else {
      // by item: every line MUST be assigned to a split idx
      const missing = order.lines.filter((l) => itemAssign[l.id] == null);
      if (missing.length) {
        toast.error(`${missing.length} item(s) non attribué(s)`);
        return;
      }
      splits = itemSplits.map((s, idx) => {
        const line_ids = order.lines.filter((l) => itemAssign[l.id] === idx).map((l) => l.id);
        const amount = order.lines
          .filter((l) => itemAssign[l.id] === idx)
          .reduce((acc, l) => acc + l.line_total, 0);
        return { label: s.label, payment_mode: s.payment_mode, amount: Number(amount.toFixed(2)), line_ids };
      }).filter((s) => s.amount > 0);
      // rebalance rounding: force sum == total
      const sum = splits.reduce((acc, s) => acc + s.amount, 0);
      const diff = Number((total - sum).toFixed(2));
      if (Math.abs(diff) >= 0.01 && splits.length) splits[splits.length - 1].amount = Number((splits[splits.length - 1].amount + diff).toFixed(2));
    }

    setSubmitting(true);
    try {
      await api.post(`/pos/orders/${order.id}/close`, { splits });
      onPaid();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur encaissement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="pos-close-dialog">
        <DialogHeader>
          <DialogTitle>Encaisser — {order.table_label}</DialogTitle>
          <DialogDescription>Total à encaisser : <b className="text-primary">{priceEur(total)}</b></DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 flex-wrap">
          {[{ k: "single", l: "Paiement unique" }, { k: "equal", l: "Split égal" }, { k: "items", l: "Split par item" }].map((m) => (
            <button
              key={m.k}
              onClick={() => setMode(m.k)}
              className={`rounded-full px-4 h-10 border ${mode === m.k ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
              data-testid={`pos-close-mode-${m.k}`}
            >
              {m.l}
            </button>
          ))}
        </div>

        {mode === "single" && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            {["cash", "card", "other"].map((pm) => (
              <button
                key={pm}
                onClick={() => setSingleMode(pm)}
                className={`rounded-xl border p-3 flex flex-col items-center gap-1 ${singleMode === pm ? "border-primary bg-primary/5" : "border-border"}`}
                data-testid={`pos-close-single-${pm}`}
              >
                {pm === "cash" ? <Banknote className="w-5 h-5" /> : pm === "card" ? <CreditCard className="w-5 h-5" /> : <Receipt className="w-5 h-5" />}
                <span className="text-xs">{PAYMENT_LABELS[pm]}</span>
              </button>
            ))}
          </div>
        )}

        {mode === "equal" && (
          <div className="grid gap-3 mt-2">
            <div>
              <Label>Nombre de parts</Label>
              <Input type="number" min="1" max="20" value={equalCount} onChange={(e) => setEqualCount(e.target.value)}
                     className="h-12 mt-1" data-testid="pos-close-equal-count" />
            </div>
            <div className="grid gap-2 max-h-52 overflow-auto">
              {Array.from({ length: Math.max(1, Number(equalCount) || 1) }).map((_, i) => {
                const n = Math.max(1, Number(equalCount) || 1);
                const each = Math.floor((total * 100) / n) / 100;
                const amt = i === n - 1 ? Number((total - each * (n - 1)).toFixed(2)) : each;
                const pm = equalModes[i] || "cash";
                return (
                  <div key={`equal-split-${n}-${i}`} className="flex items-center gap-2 rounded-lg border border-border p-2">
                    <span className="text-sm font-medium flex-1">Part {i + 1}/{n} — {priceEur(amt)}</span>
                    <select
                      value={pm}
                      onChange={(e) => setEqualModes({ ...equalModes, [i]: e.target.value })}
                      className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                      data-testid={`pos-close-equal-mode-${i}`}
                    >
                      <option value="cash">Espèces</option>
                      <option value="card">Carte</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {mode === "items" && (
          <div className="grid gap-3 mt-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Parts :</span>
              {itemSplits.map((s, idx) => (
                <span key={s.id || idx} className="rounded-full border border-border px-3 h-8 flex items-center text-sm">
                  {s.label} · {PAYMENT_LABELS[s.payment_mode]}
                </span>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setItemSplits([
                  ...itemSplits,
                  { id: `s-${Date.now()}-${itemSplits.length}`,
                    label: `Part ${itemSplits.length + 1}`, payment_mode: "cash" },
                ])}
                data-testid="pos-close-items-add-split"
              >
                <Plus className="w-3 h-3 mr-1" /> Part
              </Button>
            </div>

            {itemSplits.map((s, idx) => (
              <div key={s.id || idx} className="flex items-center gap-2">
                <Label className="w-20">Part {idx + 1}</Label>
                <select
                  value={s.payment_mode}
                  onChange={(e) => {
                    const copy = [...itemSplits];
                    copy[idx] = { ...copy[idx], payment_mode: e.target.value };
                    setItemSplits(copy);
                  }}
                  className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                  data-testid={`pos-close-items-mode-${idx}`}
                >
                  <option value="cash">Espèces</option>
                  <option value="card">Carte</option>
                  <option value="other">Autre</option>
                </select>
              </div>
            ))}

            <div className="grid gap-1 max-h-52 overflow-auto border-t border-border pt-2">
              {order.lines.map((l) => (
                <div key={l.id} className="flex items-center gap-2">
                  <span className="flex-1 text-sm truncate">{l.quantity} × {l.name} — <span className="text-primary">{priceEur(l.line_total)}</span></span>
                  <select
                    value={itemAssign[l.id] ?? ""}
                    onChange={(e) => setItemAssign({ ...itemAssign, [l.id]: e.target.value === "" ? undefined : Number(e.target.value) })}
                    className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                    data-testid={`pos-close-items-assign-${l.id}`}
                  >
                    <option value="">— Attribuer —</option>
                    {itemSplits.map((s, idx) => (
                      <option key={s.id || idx} value={idx}>{s.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="pos-close-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Encaisser ${priceEur(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
